import { useLayoutEffect, useState } from "react";
import { openFloatingElements } from "./geometry";

export interface TrackedRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function readRect(el: HTMLElement): TrackedRect {
  const r = el.getBoundingClientRect();
  return { top: r.top, left: r.left, width: r.width, height: r.height };
}

/** Smallest rect covering both, or the other when one is null. */
export function unionRect(
  a: TrackedRect | null,
  b: TrackedRect | null,
): TrackedRect | null {
  if (!a) return b;
  if (!b) return a;
  const top = Math.min(a.top, b.top);
  const left = Math.min(a.left, b.left);
  const right = Math.max(a.left + a.width, b.left + b.width);
  const bottom = Math.max(a.top + a.height, b.top + b.height);
  return { top, left, width: right - left, height: bottom - top };
}

/**
 * Rects of every floating surface a guided interaction has popped open
 * (date/batch popovers, calendars, sender/recipient/product dropdowns), each as
 * its OWN rect — NOT unioned. The backdrop stitches a separate cutout per
 * surface so the bright area hugs each element instead of ballooning to the
 * bounding box of the target + a far-away dropdown. Empty when nothing is open.
 * Recomputes on DOM mutations (open/close), scroll, and resize (all rAF-batched);
 * listeners exist only while `active`.
 */
/** Structural equality for two rect lists (same length, same geometry). */
function sameRects(a: TrackedRect[], b: TrackedRect[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((r, i) => {
    const o = b[i];
    return (
      r.top === o.top &&
      r.left === o.left &&
      r.width === o.width &&
      r.height === o.height
    );
  });
}

export function useOpenFloatingRects(active: boolean): TrackedRect[] {
  const [rects, setRects] = useState<TrackedRect[]>([]);

  useLayoutEffect(() => {
    if (!active) {
      setRects([]);
      return;
    }

    let rafId: number | null = null;
    const recompute = () => {
      rafId = null;
      const next = openFloatingElements().map(readRect);
      // Skip the state write when nothing changed — the common idle case is no
      // floating surface open, so `next` is empty and equal to the last empty
      // array. Returning `prev` unchanged lets React bail out of the re-render,
      // so scroll/mutation ticks don't churn the overlay for nothing.
      setRects((prev) => (sameRects(prev, next) ? prev : next));
    };
    const schedule = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(recompute);
    };

    schedule();
    const mutationObserver = new MutationObserver(schedule);
    mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style", "data-state", "hidden"],
    });
    window.addEventListener("scroll", schedule, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", schedule);

    return () => {
      mutationObserver.disconnect();
      window.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [active]);

  return rects;
}

/**
 * Track an element's viewport rect while a guide step is active. Listeners
 * exist ONLY for the lifetime of the step (nothing global when idle) and all
 * recomputes are rAF-batched so scroll on slow machines costs one
 * getBoundingClientRect per frame at most.
 *
 * `onDetached` fires when the element leaves the DOM (virtualized row
 * scrolled out, dialog closed) so the engine can re-resolve or fail fast.
 */
export function useTrackedRect(
  el: HTMLElement | null,
  onDetached?: () => void,
): TrackedRect | null {
  const [rect, setRect] = useState<TrackedRect | null>(null);

  useLayoutEffect(() => {
    if (!el) {
      setRect(null);
      return;
    }
    setRect(readRect(el));

    // Settle: a target can resolve MID-TRANSITION — a Radix dialog's enter
    // (transform: translate/scale) or an async-laid-out list row — which moves
    // the box WITHOUT firing ResizeObserver (size is constant) or scroll. Those
    // listeners would never correct it, leaving the ring on the animating
    // position (a contour drawn under a row / offset from a dialog field).
    // Re-read for a short window until the box holds steady so the ring lands on
    // the settled position.
    let settleRaf: number | null = null;
    let lastRect = readRect(el);
    let stableFrames = 0;
    let frames = 0;
    const settle = () => {
      settleRaf = null;
      // Detached mid-settle (virtualized row scrolled out, dialog closed) — fail
      // fast like the scroll/mutation paths below, not a silent stop that leaves
      // the ring on a stale box.
      if (!el.isConnected) {
        onDetached?.();
        return;
      }
      const next = readRect(el);
      if (
        next.top !== lastRect.top ||
        next.left !== lastRect.left ||
        next.width !== lastRect.width ||
        next.height !== lastRect.height
      ) {
        setRect(next);
        lastRect = next;
        stableFrames = 0;
      } else {
        stableFrames += 1;
      }
      frames += 1;
      // Stop once steady for a few frames; hard-capped so it can never spin
      // (~1s at 60fps).
      if (stableFrames < 3 && frames < 60) {
        settleRaf = requestAnimationFrame(settle);
      }
    };
    settleRaf = requestAnimationFrame(settle);

    let rafId: number | null = null;
    const schedule = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        if (!el.isConnected) {
          onDetached?.();
          return;
        }
        setRect(readRect(el));
      });
    };

    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(el);
    // Snap to the SETTLED box when a CSS transition/animation finishes. An
    // ANCESTOR transition — e.g. the recap content pane sliding as the chat
    // panel opens (`margin-right 300ms ease-out`) — moves `el` without resizing
    // it or firing scroll, and the rAF settle loop above can conclude "steady"
    // during the ease-out's decelerating tail (sub-pixel per-frame deltas read
    // equal for 3 frames), leaving the ring a few px shy of the final position.
    // transition/animationend fire at the true end of the motion, so re-measure
    // then. Captured at the window because the event's target is the animating
    // ancestor, whose end event never bubbles DOWN to `el`.
    window.addEventListener("transitionend", schedule, { capture: true });
    window.addEventListener("animationend", schedule, { capture: true });
    // Detect DETACH promptly. ResizeObserver/scroll/resize don't fire when the
    // element is simply removed (a popover/row closes, a route unmounts), so
    // without this the caller's shield/overlay would linger until the user
    // happens to scroll. A childList mutation anywhere that disconnects `el`
    // fails the step fast instead.
    const mutationObserver = new MutationObserver(() => {
      if (!el.isConnected) onDetached?.();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });
    // capture:true catches scrolls of any ancestor container, not just window.
    window.addEventListener("scroll", schedule, {
      capture: true,
      passive: true,
    });
    window.addEventListener("resize", schedule);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
      window.removeEventListener("transitionend", schedule, { capture: true });
      window.removeEventListener("animationend", schedule, { capture: true });
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (settleRaf !== null) cancelAnimationFrame(settleRaf);
    };
  }, [el, onDetached]);

  return rect;
}
